import React, { useState } from 'react';
import { useApp } from '../context/AppContext';

const TYPE_META = {
  highlight: { border:'#C4975A' },
  note:      { border:'#6B7FA0' },
  bookmark:  { border:'#9B8EC4' },
};

const HL_BG = { yellow:'#FBF3D0', green:'#E4F4EC', red:'#FAE8E6', blue:'#EEF2F8', purple:'#F0EDF8' };
const HL_DOT = { yellow:'#E8CFA0', green:'#A3C4B4', red:'#E8B4B1', blue:'#A8B8D0', purple:'#C4A3B4' };

const NotebooksPage = ({ onNav, onOpenBook }) => {
  const { annotations, removeAnnotation, addAnnotation } = useApp();

  const [tab,            setTab]            = useState('Highlights');
  const [filterBook,     setFilterBook]     = useState('All');
  const [sortBy,         setSortBy]         = useState('newest');
  const [view,           setView]           = useState('all');
  const [matters, setMatters] = useState(() => {
    try { return JSON.parse(localStorage.getItem('air_matter_folders') || '["General Research"]'); }
    catch { return ['General Research']; }
  });
  const [showNewMatter,  setShowNewMatter]  = useState(false);
  const [newMatterName,  setNewMatterName]  = useState('');
  const [showNewNote,    setShowNewNote]    = useState(false);
  const [newNoteText,    setNewNoteText]    = useState('');
  const [newNoteBook,    setNewNoteBook]    = useState('');
  const [toast,          setToast]          = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2200); };

  const books  = ['All', ...new Set(annotations.map(a => a.bookName).filter(Boolean))];
  const counts = {
    Highlights: annotations.filter(a => a.type === 'highlight').length,
    Notes:      annotations.filter(a => a.type === 'note').length,
    // Bookmarks:  annotations.filter(a => a.type === 'bookmark').length,
  };

  const filtered = annotations.filter(a => {
    const tabMatch =
      (tab === 'Highlights' && a.type === 'highlight') ||
      (tab === 'Notes'      && a.type === 'note')      
      // (tab === 'Bookmarks'  && a.type === 'bookmark');
    const bookMatch = filterBook === 'All' || a.bookName === filterBook;
    return tabMatch && bookMatch;
  }).sort((a, b) =>
    sortBy === 'newest'
      ? new Date(b.createdAt) - new Date(a.createdAt)
      : new Date(a.createdAt) - new Date(b.createdAt)
  );

const handleExport = (format) => {
    const stripHtml = (str) => {
      if (!str) return '';
      return str
        .replace(/<[^>]*>/g, '')      // HTML tags hatao
        .replace(/&amp;/g, '&')       // HTML entities decode karo
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')
        .replace(/Â/g, '')            // encoding artifact hatao
        .replace(/\u00A0/g, ' ')      // non-breaking space
        .trim();
    };
    const lines = filtered.map(a =>
      `[${(a.type||'').toUpperCase()}] ${a.bookName||''} · ${a.chapterTitle||''}\n` +
      `"${stripHtml(a.selectedText)}"\n` +
      (a.note ? `Note: ${stripHtml(a.note)}\n` : '') +
      `Citation: ${stripHtml(a.selectedText).substring(0,60)}… [${a.bookName||''}, AIR]\n`
    ).join('\n---\n');
    const blob = new Blob([`AIR Reader — Notebooks Export\n${'='.repeat(40)}\n\n${lines}`], { type:'text/plain' });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = `AIR_Notebooks.${format === 'CSV' ? 'csv' : 'txt'}`; link.click();
    URL.revokeObjectURL(url);
    showToast('Exported ✓');
  };

  const handleCopyCite = (ann) => {
    navigator.clipboard?.writeText(`${ann.selectedText} [${ann.bookName}, AIR]`).catch(() => {});
    showToast('AIR citation copied ✓');
  };

const handleViewInBook = (ann) => {
    if (!ann.bookId || ann.bookId === 0) { showToast('No book linked to this note'); return; }
    if (onOpenBook) onOpenBook(
      { _id: ann.bookId, book_name: ann.bookName },
      ann.chapterId,
      ann.selectedText  // ← highlight text pass karo
    );
  };

  const handleSaveNewNote = async () => {
    if (!newNoteText.trim()) return;
    await addAnnotation({
      type: 'note', color: 'blue',
      bookId: 0,
      bookName:     newNoteBook.trim() || 'Quick Note',
      chapterId:    null,
      chapterTitle: '',
      selectedText: newNoteText.trim(),
      note: null, tags: [],
    });
    setNewNoteText(''); setNewNoteBook(''); setShowNewNote(false);
    showToast('Note saved ✓');
  };

  const MatterView = () => (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', borderBottom:'1px solid var(--border)', background:'white' }}>
        <span style={{ fontSize:13, fontWeight:700, color:'var(--ink)', fontFamily:'Nunito,sans-serif' }}>Matter Folders</span>
        <button onClick={() => setShowNewMatter(true)}
          style={{ padding:'5px 10px', background:'var(--slate)', color:'white', border:'none', borderRadius:6, fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'Nunito,sans-serif' }}>
          + New Folder
        </button>
      </div>
      {showNewMatter && (
        <div style={{ display:'flex', gap:8, padding:'12px 16px', borderBottom:'1px solid var(--border)', background:'var(--off-white)' }}>
          <input value={newMatterName} onChange={e => setNewMatterName(e.target.value)}
            placeholder="e.g. Sharma v. State of MP"
            style={{ flex:1, padding:'8px 12px', borderRadius:8, border:'1px solid var(--border)', fontSize:12, fontFamily:'Nunito,sans-serif', color:'var(--ink)', outline:'none' }}
            onKeyDown={e => { if (e.key === 'Enter') { if(newMatterName.trim()) { setMatters(p=>[...p,newMatterName.trim()]); setNewMatterName(''); setShowNewMatter(false); showToast('Folder created ✓'); } } }}/>
          <button onClick={() => { if(newMatterName.trim()) {
            const next = [...matters, newMatterName.trim()];
            setMatters(next);
            try { localStorage.setItem('air_matter_folders', JSON.stringify(next)); } catch {}
            setNewMatterName(''); setShowNewMatter(false); showToast('Folder created ✓');
          } }}
            style={{ padding:'8px 12px', background:'var(--slate)', color:'white', border:'none', borderRadius:8, fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'Nunito,sans-serif' }}>Create</button>
          <button onClick={() => { setShowNewMatter(false); setNewMatterName(''); }}
            style={{ padding:'8px 12px', background:'transparent', color:'var(--ink-mid)', border:'1px solid var(--border)', borderRadius:8, fontSize:11, cursor:'pointer', fontFamily:'Nunito,sans-serif' }}>Cancel</button>
        </div>
      )}
      <div style={{ padding:16, display:'flex', flexDirection:'column', gap:10 }}>
        {matters.map(m => (
          <div key={m} onClick={() => { setView('all'); setFilterBook('All'); }}
            style={{ display:'flex', alignItems:'center', gap:12, padding:16, background:'white', borderRadius:12, border:'1px solid var(--border)', cursor:'pointer' }}
            onMouseEnter={e => e.currentTarget.style.boxShadow='var(--shadow-sm)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
            <span style={{ fontSize:24 }}>📁</span>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)', fontFamily:'Nunito,sans-serif' }}>{m}</div>
              <div style={{ fontSize:10, color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif', marginTop:2 }}>
                {annotations.length} annotations
              </div>
            </div>
            <span style={{ color:'var(--ink-lt)', fontSize:18 }}>›</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ background:'var(--off-white)', minHeight:'100vh', position:'relative', paddingBottom:64, fontFamily:'Nunito,system-ui,sans-serif' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', background:'var(--slate-pale)', borderBottom:'1px solid var(--slate-lt)' }}>
        <div>
          <div style={{ fontFamily:'Playfair Display,serif', fontSize:16, fontWeight:700, color:'var(--slate)' }}>My Notebooks</div>
          <div style={{ fontSize:9, color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif', marginTop:2 }}>{annotations.length} total annotations</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={() => setView(view === 'all' ? 'matters' : 'all')}
            style={{ padding:'5px 10px', background: view==='matters' ? 'var(--slate)' : 'rgba(107,127,160,.4)', color:'white', border:'none', borderRadius:6, fontSize:10, fontWeight:700, cursor:'pointer', fontFamily:'Nunito,sans-serif' }}>
            {view === 'matters' ? '☰ All' : '📁 Folders'}
          </button>
          <button onClick={() => setShowNewNote(true)}
            style={{ padding:'5px 10px', background:'var(--slate)', color:'white', border:'none', borderRadius:6, fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'Nunito,sans-serif' }}>
            + New
          </button>
        </div>
      </div>

      {view === 'matters' ? <MatterView/> : (
        <>
          {/* Tabs */}
          <div style={{ display:'flex', background:'white', borderBottom:'1px solid var(--border)' }}>
            {['Highlights','Notes'].map(t => (
              <button key={t} onClick={() => setTab(t)}
                style={{ flex:1, padding:'10px 0', fontSize:10, fontWeight:700, cursor:'pointer', background: tab===t ? 'var(--off-white)' : 'white',
                  color: tab===t ? 'var(--slate)' : 'var(--ink-lt)', border:'none',
                  borderBottom: tab===t ? '2px solid var(--slate)' : '2px solid transparent', fontFamily:'Nunito,sans-serif' }}>
                {t} ({counts[t]})
              </button>
            ))}
          </div>

          {/* Filters */}
          <div style={{ display:'flex', gap:8, alignItems:'center', padding:'8px 16px', background:'white', borderBottom:'1px solid var(--border)', flexWrap:'wrap' }}>
            <select value={filterBook} onChange={e => setFilterBook(e.target.value)}
              style={{ padding:'4px 8px', borderRadius:6, border:'1px solid var(--border)', fontSize:11, fontFamily:'Nunito,sans-serif', color:'var(--ink-mid)', outline:'none', cursor:'pointer' }}>
              {books.map(b => <option key={b} value={b}>{b === 'All' ? 'All Books' : b}</option>)}
            </select>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              style={{ padding:'4px 8px', borderRadius:6, border:'1px solid var(--border)', fontSize:11, fontFamily:'Nunito,sans-serif', color:'var(--ink-mid)', outline:'none', cursor:'pointer' }}>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select>
            <span style={{ marginLeft:'auto', fontSize:10, color:'var(--ink-faint)', fontFamily:'Nunito,sans-serif' }}>{filtered.length} items</span>
          </div>

          {/* List */}
          <div style={{ overflowY:'auto', maxHeight:'calc(100vh - 260px)' }}>
            {filtered.length === 0 ? (
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'64px 24px', textAlign:'center' }}>
                <div style={{ fontSize:40, opacity:.3, marginBottom:10 }}>✎</div>
                <div style={{ fontFamily:'Playfair Display,serif', fontSize:16, fontWeight:700, color:'var(--ink)', marginBottom:6 }}>No {tab.toLowerCase()} yet</div>
                <div style={{ fontSize:12, color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif' }}>
                  {tab === 'Notes' ? 'Click "+ New" to add a quick note.' : 'Open any book, select text and highlight.'}
                </div>
              </div>
            ) : filtered.map((ann, i) => {
              const hlBg  = ann.type === 'highlight' ? (HL_BG[ann.color] || '#FBF3D0') : null;
              const dot   = ann.type === 'highlight' ? (HL_DOT[ann.color] || '#E8CFA0') : ann.type === 'note' ? '#A8B8D0' : '#C4A3B4';
              const borderColor = (TYPE_META[ann.type] || TYPE_META.highlight).border;
              return (
                <div key={String(ann._id)}
                  style={{ padding:'12px 16px', borderBottom:'1px solid var(--border-lt)', background:'white', animationDelay:`${i*0.04}s` }}
                  onMouseEnter={e => e.currentTarget.style.background='var(--rose-mist)'}
                  onMouseLeave={e => e.currentTarget.style.background='white'}>

                  {/* Top row */}
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                    <div style={{ width:9, height:9, borderRadius:'50%', background:dot, flexShrink:0 }}/>
                    <div style={{ fontSize:9, fontWeight:700, color:'var(--ink-lt)', textTransform:'uppercase', letterSpacing:.5, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'Nunito,sans-serif' }}>
                      {ann.bookName}{ann.chapterTitle ? ` · ${ann.chapterTitle}` : ''}
                    </div>
                    <span style={{ fontSize:9, color:'var(--ink-faint)', fontFamily:'Nunito,sans-serif', flexShrink:0 }}>
                      {ann.createdAt ? new Date(ann.createdAt).toLocaleDateString('en-IN') : ''}
                    </span>
                    <button onClick={() => removeAnnotation(String(ann._id))}
                      style={{ fontSize:14, color:'var(--ink-faint)', background:'none', border:'none', cursor:'pointer', padding:0, flexShrink:0 }}
                      onMouseEnter={e => e.currentTarget.style.color='var(--rose)'}
                      onMouseLeave={e => e.currentTarget.style.color='var(--ink-faint)'}>🗑</button>
                  </div>

                  {/* Highlighted text with color */}
                  <div style={{
                    fontSize:12, color:'var(--ink)', lineHeight:1.6, fontFamily:'Lora,Georgia,serif',
                    padding: hlBg ? '6px 10px' : '0', borderRadius: hlBg ? 4 : 0,
                    background: hlBg || 'transparent',
                    borderLeft: hlBg ? `3px solid ${borderColor}` : 'none',
                    marginBottom:4,
                  }}>
                    "{ann.selectedText}"
                  </div>

                  {ann.note && (
                    <div style={{ fontSize:11, color:'var(--slate)', fontFamily:'Nunito,sans-serif', fontStyle:'italic', marginBottom:4 }}>
                      📝 {ann.note}
                    </div>
                  )}

                  {/* AIR citation */}
                  <div style={{ fontSize:9, fontWeight:700, color:'var(--rose)', fontFamily:'Nunito,sans-serif', marginBottom:6 }}>
                    AIR, {ann.bookName}{ann.chapterTitle ? ', '+ann.chapterTitle : ''} (AIR)
                  </div>

                  {/* Actions */}
                  <div style={{ display:'flex', gap:16 }}>
                    <button onClick={() => handleCopyCite(ann)}
                      style={{ fontSize:10, color:'var(--rose)', fontFamily:'Nunito,sans-serif', background:'none', border:'none', cursor:'pointer', fontWeight:700, padding:0 }}>
                      ⌗ Copy Citation
                    </button>
                    <button onClick={() => handleViewInBook(ann)}
                      style={{ fontSize:10, color:'var(--slate)', fontFamily:'Nunito,sans-serif', background:'none', border:'none', cursor:'pointer', fontWeight:700, padding:0 }}>
                      📖 View in Book
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Export bar */}
      <div style={{ position:'absolute', bottom:0, left:0, right:0, display:'flex', gap:8, padding:'10px 16px', background:'white', borderTop:'1px solid var(--border)' }}>
        <button onClick={() => handleExport('DOCX')} style={{ flex:1, padding:'8px', background:'var(--slate)', color:'white', border:'none', borderRadius:6, fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'Nunito,sans-serif' }}>📄 DOCX</button>
        <button onClick={() => handleExport('CSV')}  style={{ flex:1, padding:'8px', background:'var(--rose)',  color:'white', border:'none', borderRadius:6, fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'Nunito,sans-serif' }}>📕 CSV</button>
        <button onClick={() => { navigator.clipboard?.writeText(filtered.map(a=>`${a.selectedText} [${a.bookName}, AIR]`).join('\n\n')).catch(()=>{}); showToast('Copied ✓'); }}
          style={{ flex:1, padding:'8px', background:'white', color:'var(--ink-mid)', border:'1px solid var(--border)', borderRadius:6, fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'Nunito,sans-serif' }}>🔗 Share</button>
      </div>

      {/* New Note Modal */}
      {showNewNote && (
        <div style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(44,44,53,.5)' }}
          onClick={() => setShowNewNote(false)}>
          <div style={{ background:'white', borderRadius:20, padding:24, maxWidth:480, width:'90%', boxShadow:'0 8px 40px rgba(0,0,0,.15)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily:'Playfair Display,serif', fontSize:18, fontWeight:700, color:'var(--ink)', marginBottom:16 }}>New Quick Note</div>
            <input value={newNoteBook} onChange={e => setNewNoteBook(e.target.value)}
              placeholder="Book / Source (optional)"
              style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:'1px solid var(--border)', fontSize:12, fontFamily:'Nunito,sans-serif', color:'var(--ink)', outline:'none', marginBottom:12, boxSizing:'border-box' }}/>
            <textarea value={newNoteText} onChange={e => setNewNoteText(e.target.value)}
              placeholder="Write your note here…" rows={5} autoFocus
              style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:'1px solid var(--border)', fontSize:13, fontFamily:'Lora,serif', color:'var(--ink)', outline:'none', resize:'none', lineHeight:1.7, boxSizing:'border-box', marginBottom:4 }}
              onKeyDown={e => { if (e.key==='Enter' && e.ctrlKey) handleSaveNewNote(); }}/>
            <div style={{ fontSize:10, color:'var(--ink-faint)', fontFamily:'Nunito,sans-serif', textAlign:'right', marginBottom:16 }}>Ctrl+Enter to save</div>
            <div style={{ display:'flex', gap:12 }}>
              <button onClick={handleSaveNewNote}
                style={{ flex:1, padding:'10px', background:'var(--slate)', color:'white', border:'none', borderRadius:12, fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'Nunito,sans-serif' }}>
                Save Note
              </button>
              <button onClick={() => { setShowNewNote(false); setNewNoteText(''); setNewNoteBook(''); }}
                style={{ flex:1, padding:'10px', background:'transparent', color:'var(--ink-mid)', border:'1px solid var(--border)', borderRadius:12, fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'Nunito,sans-serif' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position:'fixed', bottom:80, left:'50%', transform:'translateX(-50%)', zIndex:9999, background:'var(--ink)', color:'white', padding:'10px 20px', borderRadius:12, fontSize:12, fontWeight:700, fontFamily:'Nunito,sans-serif', whiteSpace:'nowrap', boxShadow:'0 4px 20px rgba(0,0,0,.2)' }}>
          {toast}
        </div>
      )}
    </div>
  );
};

export default NotebooksPage;